import { request } from '@/lib/api'

/**
 * Задачи, встречи и календарь (#110, #112, #113).
 *
 * Права — `sales.activity`, и все решения о том, чей календарь можно открыть и чью встречу
 * перенести, принимает сервер: `viewable` у сотрудника и `editable` у события приходят с
 * ответом, а не вычисляются здесь по своей копии правила.
 */

export type ActivityEntityType = 'deal' | 'lead' | 'counterparty' | 'contact' | 'invoice'

export interface Person {
  id: string
  name?: string | null
  departmentId?: string | null
  departmentName?: string | null
  /** Календарь можно открыть (область `sales.activity:read`). Пригласить можно любого. */
  viewable: boolean
}

export interface People {
  me?: string | null
  /** `own` | `department` | `all`. */
  scope: string
  people: Person[]
  /** Пресет «неделя отдела»: свой отдел с подотделами. */
  myDepartment: string[]
  mayCreate: boolean
}

export interface Participant {
  employeeId?: string | null
  contactId?: string | null
  /** Имени контакта нет, если смотрящий не видит этот контакт. */
  name?: string | null
}

export interface CalendarEvent {
  id: string
  /** `task` | `meeting` | `call`. */
  kind: string
  subject: string
  body?: string | null
  status: string
  /** Начало встречи или срок задачи. */
  start: string
  endsAt?: string | null
  dueAt?: string | null
  location?: string | null
  entityType?: string | null
  entityId?: string | null
  entityLabel?: string | null
  assigneeEmployeeId?: string | null
  assigneeName?: string | null
  participants: Participant[]
  /** Кого из показанных людей событие касается. */
  employeeIds: string[]
  remindBeforeMinutes?: number | null
  recurrence?: string | null
  recurrenceUntil?: string | null
  overdue: boolean
  /** Можно перенести и править — решает сервер по области `update`. */
  editable: boolean
  /** Встреча пересекается с другой встречей того же человека — считает сервер. */
  conflict: boolean
}

export interface CalendarData {
  from: string
  to: string
  people: Person[]
  events: CalendarEvent[]
}

export interface EventRef {
  id: string
  kind: string
  status: string
  start?: string | null
  endsAt?: string | null
  dueAt?: string | null
}

export interface TaskInput {
  subject: string
  body?: string | null
  entityType?: ActivityEntityType | null
  entityId?: string | null
  assigneeId?: string | null
  dueAt?: string | null
  /** Не указано — значение из системных настроек; отрицательное — не напоминать. */
  remindBeforeMinutes?: number | null
  recurrence?: string | null
  recurrenceUntil?: string | null
}

export interface MeetingInput {
  subject: string
  body?: string | null
  entityType?: ActivityEntityType | null
  entityId?: string | null
  organizerId?: string | null
  startsAt: string
  endsAt?: string | null
  location?: string | null
  remindBeforeMinutes?: number | null
  employeeIds: string[]
  contactIds: string[]
}

export function fetchPeople(signal?: AbortSignal): Promise<People> {
  return request<People>('/calendar/people', { signal })
}

export function fetchCalendar(from: Date, to: Date, employees: string[], signal?: AbortSignal): Promise<CalendarData> {
  const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() })
  if (employees.length > 0) query.set('employees', employees.join(','))
  return request<CalendarData>(`/calendar?${query.toString()}`, { signal })
}

/** Перенос перетаскиванием: встреча сохраняет длительность — это делает домен. */
export function moveEvent(id: string, at: Date): Promise<EventRef> {
  return request<EventRef>(`/calendar/events/${id}/move`, { method: 'POST', body: { at: at.toISOString() } })
}

export function createTask(input: TaskInput): Promise<EventRef> {
  return request<EventRef>('/tasks', { method: 'POST', body: input })
}

export function updateTask(id: string, input: TaskInput): Promise<EventRef> {
  return request<EventRef>(`/tasks/${id}`, { method: 'PUT', body: input })
}

export function createMeeting(input: MeetingInput): Promise<EventRef> {
  return request<EventRef>('/meetings', { method: 'POST', body: input })
}

export function updateMeeting(id: string, input: MeetingInput): Promise<EventRef> {
  return request<EventRef>(`/meetings/${id}`, { method: 'PUT', body: input })
}
